// Administrative lifecycle: what each confirmation must say (documentation/administrative-lifecycle.md).
// The server decides and computes the impact; these functions only phrase it. Never a secret.
// Unified identity: people are never deleted with an OEM or customer - they lose that access, and
// people left without any access are disabled (kept). Only a customer's LEGACY network accounts
// are deleted with it.
import type { DeletedUser, OEMDeleteImpact, OEMUser } from '../../services/oemAdmin';
import type { CustomerDeleteImpact, NetworkDeleteImpact } from '../../services/tenancy';

export interface DeleteDescription {
  title: string;
  points: string[];
  confirm: string;
  /** Set when the server refuses this delete as things are now. */
  blocked?: string;
}

const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

/** Removing one person from an OEM Partner. */
export function describeOEMUserDelete(u: Pick<OEMUser, 'username' | 'is_oem_admin'> & { account?: { login_email?: string; provisioned?: boolean } }): DeleteDescription {
  const points = [
    `Person: ${u.username}${u.account?.login_email ? ` (${u.account.login_email})` : ''}.`,
    `Their access in this OEM Partner (${u.is_oem_admin ? 'OEM administrator' : 'OEM user'}) is removed now.`,
    'Access elsewhere (other OEMs, customers) is kept. A person left without any access is disabled - kept, never deleted - and their sessions end.',
    'Sign-in methods are not deleted. The administration audit keeps a record.',
  ];
  return { title: `Remove ${u.username} from the OEM Partner`, points, confirm: 'Remove from OEM' };
}

/** Deleting an OEM Partner: its customers stay and become direct RadioOS customers. */
export function describeOEMDelete(imp: OEMDeleteImpact): DeleteDescription {
  const n = imp.customers_detached.length;
  const disabled = imp.users_disabled?.length ?? 0;
  const points = [
    `This permanently deletes the OEM Partner. ${plural(imp.users.length, 'person', 'people')}` +
      (imp.administrators ? ` (${plural(imp.administrators, 'administrator')})` : '') + ' lose their access in it.',
  ];
  if (disabled) points.push(`${plural(disabled, 'person', 'people')} without any other access ${disabled === 1 ? 'is' : 'are'} disabled (kept, not deleted); their sessions end.`);
  points.push(n === 0
    ? 'No customers are assigned to this OEM Partner.'
    : `${plural(n, 'customer')} currently assigned to this OEM Partner will NOT be deleted. ${n === 1 ? 'It becomes a direct RadioOS customer' : 'They become direct RadioOS customers'} with all networks, radios, users and history unchanged: ${imp.customers_detached.map((c) => c.name).join(', ')}.`);
  points.push('This cannot be undone. The administration audit keeps a record.');
  return { title: `Delete OEM Partner "${imp.oem_partner.name}"?`, points, confirm: 'Delete OEM Partner' };
}

/** Deleting a customer: the broadest delete, confirmed by typing the exact name. */
export function describeCustomerDelete(imp: CustomerDeleteImpact): DeleteDescription {
  const points = [
    `${plural(imp.networks.length, 'network')} ${imp.networks.length ? `(${imp.networks.map((n) => n.name).join(', ')}) ` : ''}with their groups, GPS positions, SOS settings and events, recordings and messages are deleted.`,
    `${plural(imp.radios_returned, 'radio')} ${imp.radios_returned === 1 ? 'returns' : 'return'} to the rental inventory (the physical radios are kept; customer settings on them are reset).`,
    `${plural(imp.users.length, 'legacy network account')} ${imp.users.length === 1 ? 'is' : 'are'} deleted` +
      (imp.managed_accounts ? `, including ${plural(imp.managed_accounts, 'sign-in account')}` : '') +
      (imp.active_sessions ? `; ${plural(imp.active_sessions, 'active session')} end` : '') + '.',
    ...((imp.users_unassigned?.length ?? 0) > 0
      ? [`${plural(imp.users_unassigned!.length, 'person', 'people')} lose their access to this customer` +
        ((imp.users_disabled?.length ?? 0) > 0 ? `; ${plural(imp.users_disabled!.length, 'person', 'people')} without other access ${imp.users_disabled!.length === 1 ? 'is' : 'are'} disabled (kept, not deleted).` : '; they keep their other access.')]
      : []),
    `${plural(imp.departments, 'department')}, ${plural(imp.role_assignments, 'role assignment')} are deleted.`,
  ];
  if (imp.oem_partner) points.push(`The customer is detached from its OEM Partner ${imp.oem_partner.name}.`);
  points.push('Rental history and the administration audit are kept. This cannot be undone.');
  const blocked = imp.blocked
    ? `This customer cannot be deleted: the platform administrator account${(imp.platform_admins ?? []).length === 1 ? '' : 's'} ${(imp.platform_admins ?? []).map((p) => p.name).join(', ')} belong${(imp.platform_admins ?? []).length === 1 ? 's' : ''} to it. Move or remove ${(imp.platform_admins ?? []).length === 1 ? 'that account' : 'those accounts'} first.`
    : undefined;
  return { title: `Permanently delete customer "${imp.customer.name}"?`, points, confirm: 'Delete customer', blocked };
}

/** The typed confirmation must equal the customer's name exactly (no trimming of the name itself). */
export function customerNameConfirmed(typed: string, name: string): boolean {
  return typed.trim() === name && name !== '';
}

/** A network delete is confirmed by its exact current name; surrounding spaces of a legacy stored name are ignored. */
export function networkNameConfirmed(typed: string, name: string): boolean {
  return name.trim() !== '' && typed.trim() === name.trim();
}

// Readable names of the network-scoped tables the server reports (unknown tables show as-is).
const NETWORK_DATA_LABELS: Record<string, string> = {
  location_pings: 'GPS positions', sos_events: 'SOS events', call_alerts: 'call alerts', direct_messages: 'messages',
  conversations: 'conversations', recordings: 'recordings', event_log: 'event log entries', floor_events: 'PTT floor events',
  session_logs: 'session logs', presence: 'presence entries', inrico_heartbeats: 'heartbeats', api_keys: 'API keys',
  provisioning_tokens: 'provisioning tokens', usb_provisionings: 'USB installer records', model_discovery_sessions: 'model discovery sessions',
  radio_availability_statuses: 'radio statuses', organization_sos_settings: 'SOS settings', organization_sos_recipients: 'SOS recipients',
  dmr_codeplug_profiles: 'DMR codeplug profiles', radio_dmr_codeplugs: 'loaded DMR codeplugs',
};

const listed = (names: string[], max = 8) =>
  names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} and ${names.length - max} more`;

/** Deleting one network of a customer, confirmed by typing its exact name. */
export function describeNetworkDelete(imp: NetworkDeleteImpact): DeleteDescription {
  const n = imp.radios_returned;
  const g = imp.groups.length;
  const points = [
    `Customer ${imp.customer.name} is kept, with its departments and other networks.`,
    n === 0
      ? 'No radios are rented to this network.'
      : `${plural(n, 'radio')} ${n === 1 ? 'returns' : 'return'} to the rental inventory (the physical radios are kept; customer settings on them are reset): ${listed(imp.radios.map((r) => r.radio_id))}.`,
    g === 0 ? 'The network has no groups.' : `${plural(g, 'group')} ${g === 1 ? 'is' : 'are'} deleted with ${g === 1 ? 'its' : 'their'} memberships: ${listed(imp.groups.map((x) => x.name))}.`,
  ];
  if (imp.department_scopes) points.push(`${plural(imp.department_scopes, 'department scope')} of this network's radios and groups ${imp.department_scopes === 1 ? 'is' : 'are'} removed.`);
  if (imp.data.length) points.push(`Network data deleted with it: ${imp.data.map((d) => `${d.rows} ${NETWORK_DATA_LABELS[d.table] ?? d.table}`).join(', ')}.`);
  points.push('Rental history and the administration audit are kept. This cannot be undone.');
  const u = imp.users.length;
  const blocked = imp.blocked
    ? `This network cannot be deleted: ${plural(u, 'user')} ${u === 1 ? 'has' : 'have'} it as home network (${listed(imp.users.map((x) => x.name))}). ` +
      `Deleting the network would delete ${u === 1 ? 'that account' : 'those accounts'}; move or remove ${u === 1 ? 'it' : 'them'} first.`
    : undefined;
  return { title: `Permanently delete network "${imp.network.name}"?`, points, confirm: 'Delete network', blocked };
}

/** Short summary of a completed delete for the notice banner. */
export function deletedNotice(kind: 'user' | 'oem' | 'customer' | 'network', detail: { user?: DeletedUser; oem?: OEMDeleteImpact; customer?: CustomerDeleteImpact; network?: NetworkDeleteImpact }): string {
  if (kind === 'user' && detail.user) return `${detail.user.username} removed from the OEM Partner.`;
  if (kind === 'oem' && detail.oem) {
    const n = detail.oem.customers_detached.length;
    return `OEM Partner ${detail.oem.oem_partner.name} deleted. ${plural(detail.oem.users.length, 'person', 'people')} lost access; ${plural(n, 'customer')} now direct RadioOS customer${n === 1 ? '' : 's'}.`;
  }
  if (kind === 'customer' && detail.customer) {
    return `Customer ${detail.customer.customer.name} deleted. ${plural(detail.customer.radios_returned, 'radio')} returned to inventory.`;
  }
  if (kind === 'network' && detail.network) {
    return `Network ${detail.network.network.name} deleted. ${plural(detail.network.radios_returned, 'radio')} returned to inventory.`;
  }
  return 'Deleted.';
}
